import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Download, 
  Users, 
  Ship, 
  Calendar, 
  TrendingUp,
  CheckCircle,
  Loader2,
  FileBarChart,
  FileSpreadsheet,
  ClipboardList
} from 'lucide-react';
import { crewApi } from '../services/api';
import toast from 'react-hot-toast';

interface ReportType {
  id: string;
  name: string;
  description: string;
  icon: any;
  endpoint: string;
  category: 'crew' | 'vessel' | 'relief' | 'analytics';
}

const reports: ReportType[] = [
  {
    id: 'active-crew',
    name: 'Active Crew Report',
    description: 'Complete list of all active crew members with vessel assignments',
    icon: Users,
    endpoint: '/api/reports/active-crew',
    category: 'crew'
  },
  {
    id: 'available-crew',
    name: 'Available Crew Report',
    description: 'List of crew members available for assignment',
    icon: CheckCircle,
    endpoint: '/api/reports/available-crew',
    category: 'crew'
  },
  {
    id: 'crew-by-rank',
    name: 'Crew by Rank Report',
    description: 'Crew distribution and statistics organized by rank',
    icon: FileBarChart,
    endpoint: '/api/reports/crew-by-rank',
    category: 'crew'
  },
  {
    id: 'crew-experience',
    name: 'Crew Experience Matrix',
    description: 'Detailed experience breakdown by vessel type and rank',
    icon: TrendingUp,
    endpoint: '/api/reports/crew-experience',
    category: 'crew'
  },
  {
    id: 'vessel-fleet',
    name: 'Fleet Overview Report',
    description: 'Complete vessel fleet with categories and assignments',
    icon: Ship,
    endpoint: '/api/reports/vessels',
    category: 'vessel'
  },
  {
    id: 'vessel-crew-status',
    name: 'Vessel Crew Status',
    description: 'Current crew complement status for all vessels',
    icon: ClipboardList,
    endpoint: '/api/reports/vessel-crew-status',
    category: 'vessel'
  },
  {
    id: 'relief-plans-pending',
    name: 'Pending Relief Plans',
    description: 'All pending crew reliefs with due dates',
    icon: Calendar,
    endpoint: '/api/reports/relief-plans/pending',
    category: 'relief'
  },
  {
    id: 'relief-plans-upcoming',
    name: 'Upcoming Reliefs (30 Days)',
    description: 'Crew reliefs scheduled within the next 30 days',
    icon: Calendar,
    endpoint: '/api/reports/relief-plans/upcoming',
    category: 'relief'
  },
  {
    id: 'relief-plans-overdue',
    name: 'Overdue Relief Plans',
    description: 'Relief plans that are past their due date',
    icon: Calendar,
    endpoint: '/api/reports/relief-plans/overdue',
    category: 'relief'
  },
  {
    id: 'monthly-summary',
    name: 'Monthly Operations Summary',
    description: 'Comprehensive monthly report with all key metrics',
    icon: FileSpreadsheet,
    endpoint: '/api/reports/monthly-summary',
    category: 'analytics'
  },
  {
    id: 'crew-movements',
    name: 'Crew Movement Report',
    description: 'All crew assignments and transfers in date range',
    icon: TrendingUp,
    endpoint: '/api/reports/crew-movements',
    category: 'analytics'
  },
];

const categoryLabels = {
  crew: 'Crew Reports',
  vessel: 'Vessel Reports',
  relief: 'Relief Plan Reports',
  analytics: 'Analytics & Summary Reports'
};

const categoryColors = {
  crew: 'blue',
  vessel: 'blue',
  relief: 'blue',
  analytics: 'blue'
};

export default function ReportsPage() {
  const [downloadingReports, setDownloadingReports] = useState<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<string | 'all'>('all');

  const handleDownloadReport = async (report: ReportType) => {
    setDownloadingReports(prev => new Set(prev).add(report.id));
    
    try {
      toast.loading(`Generating ${report.name}...`, { id: report.id });
      
      const response = await fetch(`${import.meta.env.VITE_API_URL || ''}${report.endpoint}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to generate report');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.id}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`${report.name} downloaded successfully!`, { id: report.id });
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Failed to download ${report.name}`, { id: report.id });
    } finally {
      setDownloadingReports(prev => {
        const newSet = new Set(prev);
        newSet.delete(report.id);
        return newSet;
      });
    }
  };

  const filteredReports = selectedCategory === 'all' 
    ? reports 
    : reports.filter(r => r.category === selectedCategory);

  const groupedReports = filteredReports.reduce((acc, report) => {
    if (!acc[report.category]) {
      acc[report.category] = [];
    }
    acc[report.category].push(report);
    return acc;
  }, {} as Record<string, ReportType[]>);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports & Downloads</h1>
          <p className="text-gray-600 mt-1">
            Generate and download PDF reports for crew, vessels, and operations
          </p>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg"
        >
          <FileText className="w-8 h-8 text-white" />
        </motion.div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            selectedCategory === 'all'
              ? 'bg-gray-900 text-white shadow-lg'
              : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
          }`}
        >
          All Reports
        </button>
        {Object.entries(categoryLabels).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setSelectedCategory(key)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              selectedCategory === key
                ? `bg-${categoryColors[key as keyof typeof categoryColors]}-500 text-white shadow-lg`
                : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Reports Grid by Category */}
      {Object.entries(groupedReports).map(([category, categoryReports]) => (
        <div key={category} className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {categoryLabels[category as keyof typeof categoryLabels]}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categoryReports.map((report) => {
              const Icon = report.icon;
              const isDownloading = downloadingReports.has(report.id);
              const color = categoryColors[report.category];

              return (
                <motion.div
                  key={report.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -4 }}
                  className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg bg-${color}-100 flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-6 h-6 text-${color}-600`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {report.name}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        {report.description}
                      </p>
                      <button
                        onClick={() => handleDownloadReport(report)}
                        disabled={isDownloading}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                          isDownloading
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : `bg-${color}-50 text-${color}-600 hover:bg-${color}-100`
                        }`}
                      >
                        {isDownloading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            Download PDF
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Quick Stats */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
        <div className="flex items-center gap-3 mb-4">
          <FileBarChart className="w-6 h-6 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Report Information</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-600">{reports.length}</div>
            <div className="text-sm text-gray-600">Available Reports</div>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">PDF</div>
            <div className="text-sm text-gray-600">Format</div>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-600">Real-time</div>
            <div className="text-sm text-gray-600">Data</div>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="text-2xl font-bold text-orange-600">Auto</div>
            <div className="text-sm text-gray-600">Generated</div>
          </div>
        </div>
      </div>
    </div>
  );
}
